<div class="interactive-plan-container">
  <!-- Main Layout Container -->
  <div class="bg-white rounded-lg shadow-lg overflow-hidden">
    <!-- Preview Header -->
    <div class="bg-gray-50 px-6 py-4 border-b border-gray-200">
      <div class="flex items-center justify-between">
        <h4 class="text-lg font-semibold text-gray-800">Plan Vorschau</h4>
        <div class="flex items-center space-x-2">
          <button 
            mat-icon-button 
            size="small" 
            class="text-gray-600 hover:text-gray-800"
            (click)="zoomOut()"
            [disabled]="stage?.scaleX() <= planConfig.minZoom"
            title="Verkleinern">
            <mat-icon>zoom_out</mat-icon>
          </button>
          
          <span class="text-sm font-medium text-gray-700 min-w-[3rem] text-center">
            {{ planState.zoomLevel | number:'1.0-0' }}%
          </span>
          
          <button 
            mat-icon-button 
            size="small" 
            class="text-gray-600 hover:text-gray-800"
            (click)="zoomIn()"
            [disabled]="stage?.scaleX() >= planConfig.maxZoom"
            title="Vergrößern">
            <mat-icon>zoom_in</mat-icon>
          </button>
          
          <button 
            mat-icon-button 
            size="small" 
            class="text-gray-600 hover:text-gray-800"
            (click)="resetZoom()"
            title="Zoom zurücksetzen">
            <mat-icon>fullscreen</mat-icon>
          </button>
        </div>
      </div>
    </div>

    <!-- Preview Content -->
    <div class="flex h-[600px]">
      <!-- Plan Image Preview Area (Left) -->
      <div class="flex-1 bg-gray-100 p-6">
        <div class="h-full bg-white rounded-lg shadow-inner border-2 border-dashed border-gray-300 flex items-center justify-center">
          <!-- Konva Canvas Container -->
          <div 
            *ngIf="planImageUrl"
            #konvaContainer 
            class="konva-canvas w-full h-full"
            [style.width.px]="planConfig.width"
            [style.height.px]="planConfig.height">
          </div>
          
          <!-- Fallback when no image available -->
          <div *ngIf="!planImageUrl" class="text-center">
            <mat-icon class="text-gray-400 text-6xl mb-4">picture_as_pdf</mat-icon>
            <h5 class="text-lg font-medium text-gray-700 mb-2">Plan Vorschau</h5>
            <p class="text-gray-500 text-sm">Kein Bild verfügbar</p>
          </div>
        </div>
      </div>

      <!-- Indicators Sidebar (Right) -->
      <div class="w-80 bg-gray-50 border-l border-gray-200 flex flex-col">
        <div class="p-4 flex-shrink-0">
          <h5 class="text-sm font-semibold text-gray-700 mb-4 flex items-center">
            <mat-icon class="text-sm mr-2 text-blue-600">sensors</mat-icon>
            Raum-Indikatoren
          </h5>
        </div>
        
        <!-- Scrollable Indicators List -->
        <div class="flex-1 overflow-y-auto px-4 pb-4">
          <div class="space-y-2">
            <div *ngFor="let indicator of roomIndicators; let i = index" 
                 class="bg-white rounded-lg p-3 shadow-sm border border-gray-200 cursor-move hover:shadow-md transition-shadow"
                 [attr.draggable]="true"
                 (dragstart)="onDragStart($event, indicator)">
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-2">
                  <!-- Indikator Symbol -->
                  <div class="w-6 h-6 flex-shrink-0">
                    <img *ngIf="getIndicatorSymbolUrl(indicator)" 
                         [src]="getIndicatorSymbolUrl(indicator)" 
                         [alt]="indicator.indikator?.code || 'Symbol'"
                         class="w-full h-full object-contain">
                    <mat-icon *ngIf="!getIndicatorSymbolUrl(indicator)" class="text-sm text-gray-400">help_outline</mat-icon>
                  </div>
                  <div>
                    <p class="text-sm font-medium text-gray-800">
                      <span class="text-xs text-blue-600 font-mono mr-1">#{{ getFormattedIndicatorNumber(indicator.indikator_number) }}</span>
                      {{ indicator.name }}
                    </p>
                    <p class="text-xs text-gray-500">{{ indicator.room?.name || '-' }}</p>
                  </div>
                </div>
                <div class="text-right">
                  <p class="text-sm font-semibold text-gray-800">{{ indicator.current_value }} {{ indicator.einheit }}</p>
                  <div class="flex items-center justify-end space-x-2 mt-1">
                    <span class="text-xs text-gray-500">SW: {{ indicator.schwellenwert }}</span>
                    <span class="text-xs text-gray-500">{{ indicator.einheit }}</span>
                  </div>
                  <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-1"
                        [ngClass]="getIndicatorStatusBadgeClasses(indicator.status)">
                    {{ getIndicatorStatusTextSimple(indicator.status) }}
                  </span>
                </div>
              </div>
            </div>
            
            <!-- Empty state -->
            <div *ngIf="!roomIndicators || roomIndicators.length === 0" class="text-center py-8">
              <mat-icon class="text-gray-400 text-4xl mb-2">sensors_off</mat-icon>
              <p class="text-sm text-gray-500">Keine Raum-Indikatoren verfügbar</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Preview Footer with Save Button -->
    <div class="bg-gray-50 px-6 py-4 border-t border-gray-200">
      <div class="flex justify-end space-x-2">
        <button 
          mat-stroked-button 
          size="small"
          (click)="exportAsImage('png')"
          title="Als PNG exportieren">
          <mat-icon>image</mat-icon>
          PNG
        </button>
        
        <button 
          mat-stroked-button 
          size="small"
          (click)="exportAsImage('jpeg')"
          title="Als JPEG exportieren">
          <mat-icon>image</mat-icon>
          JPEG
        </button>
        
        <button 
          mat-stroked-button 
          size="small"
          (click)="exportAsJSON()"
          title="Als JSON exportieren">
          <mat-icon>code</mat-icon>
          JSON
        </button>
      </div>
    </div>
  </div>

  <!-- Tooltip -->
  <div 
    *ngIf="planState.showTooltip && planState.tooltipMarker" 
    class="tooltip"
    [style.left.px]="planState.tooltipPosition.x + 10"
    [style.top.px]="planState.tooltipPosition.y - 10"
    (click)="hideTooltip()">
    
    <div class="tooltip-content">
      <div class="tooltip-header">
        <div class="tooltip-symbol">
          <div class="symbol-placeholder"></div>
        </div>
        <div class="tooltip-info">
          <div class="tooltip-number">
            #{{ getFormattedIndicatorNumber(planState.tooltipMarker.indicator.indikator_number) }}
          </div>
          <div class="tooltip-type">
            {{ planState.tooltipMarker.indicator.indikator?.code || 'Unbekannt' }}
          </div>
        </div>
      </div>
      
      <div class="tooltip-body">
        <div class="tooltip-name">
          {{ planState.tooltipMarker.indicator.name }}
        </div>
        <div class="tooltip-room">
          {{ planState.tooltipMarker.indicator.room?.name || '-' }}
        </div>
        <div class="tooltip-values">
          <span class="tooltip-schwellenwert">
            SW: {{ planState.tooltipMarker.indicator.schwellenwert }} {{ planState.tooltipMarker.indicator.einheit }}
          </span>
          <span class="tooltip-current">
            {{ planState.tooltipMarker.indicator.current_value }} {{ planState.tooltipMarker.indicator.einheit }}
          </span>
        </div>
      </div>
      
      <div class="tooltip-footer">
        Klicken zum Schließen
      </div>
    </div>
  </div>
</div>
